/**
 * `clipboardCommands` (W1-10b core-commands-motion; DESIGN.md §5.4, §4.10; rpe-editor-features.md E§3.10, E§4.3).
 *
 * Copy (pure, no transaction): notes and motion slices with times relative to the earliest copied item. A track's
 * slice runs from its first to its last selected key / segment, so its edges are keys and their values are copied
 * exactly: the selection names keys and segments, never a beat inside a segment, so no edge value has to be
 * sampled (the contract's "edge values sampled exactly" holds trivially; CR-W1-10b-6). An edge key selected as a
 * point keeps its jump (both values), otherwise the slice starts with the key's leave value and ends with its arrive
 * value. Whole lines (Lines panel) are copied with every track, note and asset reference.
 *
 * Paste: the earliest item lands on `anchor` (or the original beats). Keys go to the same property and layer of the
 * target line (or of their original lines); inside the pasted span they REPLACE the target curve and outside it the
 * curve is preserved (shape-preserving splits at the span edges first; motionTime.ts `replaceSpan`; an edge split
 * that is only approximate is noted, as for an inserted key). A target whose lead-in Phira extrapolates from its
 * first segment (an imported lead-in, first key after beat 0) gets that lead-in as an explicit segment from beat 0
 * first when the span starts at or before its first key (exact for a straight first segment, otherwise noted), so
 * the curve before the span does not follow the pasted segment. Chart time starts at beat 0: notes that would land
 * before it are skipped, and a slice reaching before it is cut there (split shape-preservingly; its keys before 0
 * are skipped), so the pasted items before beat 0 are counted in `unmatched` on every kind of property. Pasted keys
 * are authored keys: a slice holding an ease the target track does not allow (`Ease.validateFor`, as setEase: a
 * Bezier handle x outside 0..1, a slice Phira would divide by zero in, a Bezier on speed, an overshoot on a type-on /
 * erase text; clipboard text from elsewhere is untrusted) is skipped and counted, and the first reason is noted.
 * Variants: mirrored (notes x := -x; key values negated except opacity, colour and text (DESIGN.md §5.4), and except
 * brush size and GIF progress, which have no direction: a negative brush clears the line, a negative progress
 * shows the first frame), add values (RPE numeric paste: clipboard values added to the target keys at the same
 * beats, inside the slice only: its first key adds to the leave value and its last key to the arrive value, so the
 * curve outside the span is unchanged, except after the target's last key, which holds the new end when it had no
 * jump; a single key adds to both; linked segments follow; an extrapolated lead-in fed by a changed first segment
 * is made explicit first; the rest is counted as unmatched), as new layer (a free layer with a beat-0 Stay key of 0;
 * on X/Y-linked lines both position axes of a source layer go to one layer that is free on both), repeat N times
 * every `every` beats. On X/Y-linked target lines both position axes keep their keys at the same beats: after the
 * paste each axis gets a shape-preserving key at every beat the other one has inside the pasted spans (link.ts
 * `alignXyBeats`; an absent axis is created as a constant 0).
 *
 * Whole lines paste as NEW lines: parents remapped inside the pasted set, groups matched by name (created when
 * missing), link groups renumbered, references to assets this project does not have are dropped and reported.
 */

import { Ease } from '../ease/index';
import { valueEquals } from '../ops/equal';
import { isTrackValueOf } from '../model/invariants';
import { hasGifTexture, isGifAsset, isUiMover, kindOfLine } from '../model/lineKind';
import { PROP_SPECS, propAppliesTo, requiresLeadInKey } from '../model/props';
import type {
  AssetId,
  GroupId,
  Hitsound,
  KeyId,
  Line,
  LineId,
  LineProp,
  Note,
  NoteId,
  Track,
  TrackId,
  TrackOwner,
  TrackValue,
} from '../model/types';
import type { TxBuilder } from '../ops/types';
import type { FailReason } from '../util/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { parseClipText, serializeClip, CLIP_FORMAT } from './clipboardFormat';
import {
  alignXyBeats,
  cleanLinks,
  linkedNeighbours,
  nextLinkId,
  syncLinkGroups,
  xyPartnerOf,
  type XyWindow,
} from './link';
import { freeLayer, layersInUse } from './layers';
import {
  addValue,
  commitKeys,
  createTrack,
  easeUseFor,
  indexAt,
  infoOf,
  MOTION_MSG,
  negateNumber,
  newRec,
  ownerInfo,
  readKeys,
  reason,
  requireTrack,
  sameValue,
  setArrive,
  tidy,
  type KeyRec,
  type MotionView,
  type TrackInfo,
} from './motionTrack';
import { collectSplit, notePartnerSplit, noteWorstSplit, worseSplitOn, type SplitOn } from './motionNotes';
import { explicitLeadIn, replaceSpan, type SliceKey } from './motionTime';
import { curveCtx, EXACT_SPLIT, insertKeyRec, worseSplit, type CurveCtx, type SplitReport } from './splitKey';
import type {
  ClipboardCommands,
  ClipboardPayload,
  ClipLine,
  ClipNote,
  ClipTrackSlice,
  DocView,
  PasteMode,
} from './types';

type ClipKey = ClipTrackSlice['keys'][number];

/** A closed beat span [s, e]. */
type Span = readonly [Beat, Beat];

/** Properties whose values a mirrored paste negates (every directional numeric property; see the file header). */
export const MIRROR_PROPS: ReadonlySet<LineProp> = new Set<LineProp>([
  'moveX',
  'moveY',
  'rotate',
  'speed',
  'scaleX',
  'scaleY',
  'incline',
]);

// ---------------------------------------------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------------------------------------------

function clipNoteOf(n: Note): Omit<Note, 'id' | 'line' | 'gen'> {
  return {
    kind: n.kind,
    t: n.t,
    end: n.end,
    x: n.x,
    above: n.above,
    fake: n.fake,
    speed: n.speed,
    yOffset: n.yOffset,
    size: n.size,
    alpha: n.alpha,
    visibleTime: n.visibleTime,
    hitsound: n.hitsound,
    tint: n.tint,
    tintHitEffects: n.tintHitEffects,
    judgeArea: n.judgeArea,
  };
}

function wholeNoteOf(n: Note): Omit<Note, 'id' | 'line' | 'gen'> {
  const base = clipNoteOf(n);
  return n.src === undefined ? base : { ...base, src: n.src };
}

function clipLineOf(view: MotionView, line: Line, slots: ReadonlyMap<LineId, number>): ClipLine {
  const { id: _id, parent, group, ...rest } = line;
  const tracks: ClipLine['tracks'][number][] = [];
  for (const ids of view.idx.tracksOfLine(line.id).values()) {
    for (const id of ids) {
      const t = view.doc.tracks.get(id);
      if (t?.owner.kind !== 'line') continue;
      const keys = readKeys(view, id).map((k) => ({ t: k.t, vIn: k.vIn, vOut: k.vOut, ease: k.ease, link: k.link }));
      if (keys.length > 0) tracks.push({ prop: t.owner.prop, layer: t.owner.layer, keys });
    }
  }
  const notes: Omit<Note, 'id' | 'line' | 'gen'>[] = [];
  const assets: AssetId[] = [];
  if (line.texture !== null) assets.push(line.texture);
  for (const id of view.idx.notesByLine(line.id)) {
    const n = view.doc.notes.get(id);
    if (n === undefined) continue;
    notes.push(wholeNoteOf(n));
    if (n.hitsound?.kind === 'asset' && !assets.includes(n.hitsound.asset)) assets.push(n.hitsound.asset);
  }
  // The source records travel along (CR-W2-07-1): a paste into another project can add the ones it lacks.
  const assetRefs = assets.flatMap((a) => {
    const rec = view.doc.assets.get(a);
    return rec === undefined ? [] : [rec];
  });
  return {
    line: rest,
    parentSlot: parent === null ? null : (slots.get(parent) ?? null),
    groupName: group === null ? null : (view.doc.groups.get(group)?.name ?? null),
    tracks,
    notes,
    assets,
    ...(assetRefs.length > 0 ? { assetRefs } : {}),
  };
}

function byExportOrder(view: MotionView, ids: Iterable<LineId>): LineId[] {
  return [...new Set(ids)]
    .filter((id) => view.doc.lines.has(id))
    .sort((a, b) => view.idx.lineIndex(a) - view.idx.lineIndex(b));
}

const copy: ClipboardCommands['copy'] = (view: DocView, sel) => {
  // Whole lines.
  const whole = byExportOrder(view, sel.lines ?? []);
  const wholeSlots = new Map(whole.map((id, i) => [id, i] as const));
  const wholeLines = whole.map((id) => clipLineOf(view, view.doc.lines.get(id) as Line, wholeSlots));

  // Notes.
  const notes = [...new Set(sel.notes)].map((id) => view.doc.notes.get(id)).filter((n): n is Note => n !== undefined);

  // Motion slices.
  const points = new Set(sel.keys);
  const segs = new Set(sel.segments);
  const perTrack = new Map<Track['id'], { lo: number; hi: number }>();
  // Position of each key in its track, built once per touched track (large selections stay linear).
  const positions = new Map<Track['id'], Map<KeyId, number>>();
  const positionOf = (track: Track['id'], id: KeyId): number => {
    let pos = positions.get(track);
    if (pos === undefined) {
      pos = new Map(view.idx.keysByTrack(track).map((k, i) => [k, i] as const));
      positions.set(track, pos);
    }
    return pos.get(id) ?? -1;
  };
  const touch = (id: KeyId, asSegment: boolean): void => {
    const k = view.doc.keys.get(id);
    if (k === undefined) return;
    const t = view.doc.tracks.get(k.track);
    if (t?.owner.kind !== 'line') return;
    const ids = view.idx.keysByTrack(k.track);
    const i = positionOf(k.track, id);
    if (i < 0) return;
    const hi = asSegment ? i + 1 : i;
    if (hi >= ids.length) return;
    const cur = perTrack.get(k.track);
    perTrack.set(k.track, cur === undefined ? { lo: i, hi } : { lo: Math.min(cur.lo, i), hi: Math.max(cur.hi, hi) });
  };
  for (const id of points) touch(id, false);
  for (const id of segs) touch(id, true);

  const lineSet = new Set<LineId>();
  for (const n of notes) lineSet.add(n.line);
  for (const id of perTrack.keys()) {
    const o = view.doc.tracks.get(id)?.owner;
    if (o?.kind === 'line') lineSet.add(o.line);
  }
  if (notes.length === 0 && perTrack.size === 0 && wholeLines.length === 0) return null;
  const lines = byExportOrder(view, lineSet);
  const slotOf = new Map(lines.map((id, i) => [id, i] as const));

  const bounds: { origin: Beat | null; last: Beat | null } = { origin: null, last: null };
  const widen = (a: Beat, b: Beat): void => {
    if (bounds.origin === null || Beats.lt(a, bounds.origin)) bounds.origin = a;
    if (bounds.last === null || Beats.lt(bounds.last, b)) bounds.last = b;
  };
  for (const n of notes) widen(n.t, n.end);
  const raw: { slot: number; prop: LineProp; layer: number; keys: KeyRec[] }[] = [];
  for (const [trackId, { lo, hi }] of perTrack) {
    const t = view.doc.tracks.get(trackId);
    if (t?.owner.kind !== 'line') continue;
    const keys = readKeys(view, trackId).slice(lo, hi + 1);
    const first = keys[0];
    const end = keys[keys.length - 1];
    if (first === undefined || end === undefined) continue;
    // Edge keys keep their jump only when selected as points.
    if (first.id === null || !points.has(first.id)) first.vIn = first.vOut;
    if (keys.length > 1 && (end.id === null || !points.has(end.id))) end.vOut = end.vIn;
    widen(first.t, end.t);
    raw.push({ slot: slotOf.get(t.owner.line) ?? 0, prop: t.owner.prop, layer: t.owner.layer, keys });
  }
  const o: Beat = bounds.origin ?? Beats.ZERO;
  const span = bounds.last === null ? Beats.ZERO : Beats.sub(bounds.last, o);
  const rel = (b: Beat): Beat => Beats.sub(b, o);
  const clipNotes: ClipNote[] = notes
    .sort((a, b) => Beats.cmp(a.t, b.t) || a.id - b.id)
    .map((n) => {
      const c = clipNoteOf(n);
      return { lineSlot: slotOf.get(n.line) ?? 0, note: { ...c, t: rel(c.t), end: rel(c.end) } };
    });
  const tracks: ClipTrackSlice[] = raw.map((r) => ({
    lineSlot: r.slot,
    prop: r.prop,
    layer: r.layer,
    keys: r.keys.map((k) => ({ t: rel(k.t), vIn: k.vIn, vOut: k.vOut, ease: k.ease, link: k.link })),
  }));
  return {
    format: CLIP_FORMAT,
    origin: o,
    span,
    notes: clipNotes,
    tracks,
    lines: lines.map((id) => ({ sourceLine: id, name: view.doc.lines.get(id)?.name ?? '' })),
    wholeLines,
  };
};

// ---------------------------------------------------------------------------------------------------------------
// Paste
// ---------------------------------------------------------------------------------------------------------------

function mirrorValue(v: TrackValue, prop: LineProp): TrackValue {
  return MIRROR_PROPS.has(prop) ? negateNumber(v) : v;
}

/** The beats of a key list, by `Beats.key`. */
function beatSet(keys: readonly KeyRec[]): Set<string> {
  return new Set(keys.map((k) => Beats.key(k.t)));
}

/** True when adding `b` to `a` changes it. */
function addChanges(a: TrackValue, b: TrackValue, info: TrackInfo): boolean {
  const v = addValue(a, b, info.valueType);
  return v !== null && !sameValue(v, a);
}

/**
 * The part of a pasted slice at or after beat 0, where chart time starts: keys before 0 are dropped (counted), and
 * a segment crossing beat 0 is split there shape-preservingly (evaluated with the target track's rules).
 */
function clipAtZero(
  keys: readonly SliceKey[],
  ctx: CurveCtx,
): { readonly keys: readonly SliceKey[]; readonly dropped: number; readonly report: SplitReport } {
  const first = keys[0];
  if (first === undefined || Beats.cmp(first.t, Beats.ZERO) >= 0) return { keys, dropped: 0, report: EXACT_SPLIT };
  const dropped = keys.filter((k) => Beats.cmp(k.t, Beats.ZERO) < 0).length;
  const last = keys[keys.length - 1] as SliceKey;
  if (Beats.cmp(last.t, Beats.ZERO) < 0) return { keys: [], dropped, report: EXACT_SPLIT };
  const recs = keys.map((k) => newRec(k.t, k.vIn, k.vOut, k.ease, k.link));
  const report = insertKeyRec(recs, Beats.ZERO, ctx, new Set<number>());
  const kept = recs
    .filter((k) => Beats.cmp(k.t, Beats.ZERO) >= 0)
    .map((k): SliceKey => ({ t: k.t, vIn: k.vIn, vOut: k.vOut, ease: k.ease, link: k.link }));
  return { keys: kept, dropped, report };
}

/**
 * The first reason an ease of a pasted slice is not allowed on the target track (`Ease.validateFor` with the use
 * of each segment: general, speed mode, type-on / erase text), or null. The last key's ease starts no segment
 * inside the slice but is stored too, so it is checked against its own value.
 */
function sliceEaseReason(view: MotionView, info: TrackInfo, keys: readonly SliceKey[]): FailReason | null {
  for (let j = 0; j < keys.length; j++) {
    const k = keys[j] as SliceKey;
    const end = keys[j + 1]?.vIn ?? k.vOut;
    const why = Ease.validateFor(k.ease, easeUseFor(view, info, k.vOut, end));
    if (why !== null) return why;
  }
  return null;
}

function hitsoundAvailable(tx: TxBuilder, h: Hitsound | null): boolean {
  return h === null || h.kind === 'builtin' || tx.doc.assets.has(h.asset);
}

const paste: ClipboardCommands['paste'] = (tx, { payload, anchor, targetLine, mode, xShift }) => {
  const m: PasteMode = mode;
  if (!Number.isSafeInteger(m.repeat) || m.repeat < 1) tx.fail(reason(MOTION_MSG.pasteRepeatInvalid));
  if (m.every !== undefined && !Beats.lt(Beats.ZERO, m.every)) tx.fail(reason(MOTION_MSG.pasteRepeatInvalid));
  if (!Number.isFinite(xShift)) tx.fail(reason(MOTION_MSG.valueNotFinite));
  if (payload.notes.length === 0 && payload.tracks.length === 0) tx.fail(reason(MOTION_MSG.pasteNothing));
  const every: Beat = m.every ?? (Beats.lt(Beats.ZERO, payload.span) ? payload.span : Beats.ONE);
  const base = m.originalBeats ? payload.origin : anchor;
  const lineFor = (slot: number): LineId | null => {
    if (m.toOriginalLines) {
      const src = payload.lines[slot]?.sourceLine;
      return src !== undefined && tx.doc.lines.has(src) ? src : null;
    }
    return tx.doc.lines.has(targetLine) ? targetLine : null;
  };
  if (!m.toOriginalLines && !tx.doc.lines.has(targetLine)) tx.fail(reason(MOTION_MSG.lineMissing));

  const newNotes: NoteId[] = [];
  /** Keys add-values changed (selected afterwards). */
  const pastedKeys: KeyId[] = [];
  /** Spans whose keys a slice replaced (their keys are selected afterwards, once every slice is in place). */
  const pastedSpans: { readonly track: TrackId; readonly s: Beat; readonly e: Beat }[] = [];
  /** Keys whose values add-values changed (their linked segments follow). */
  const added: KeyId[] = [];
  /** Add-values keys without a target key at their beat. */
  let unmatched = 0;
  /** Items with no place here: their line is missing, the property does not apply, or before beat 0. */
  let skipped = 0;
  let uiMoverSkipped = 0;
  let hitsoundsDropped = 0;
  /** Why the first slice with an ease the target does not allow was skipped (counted in `skipped`). */
  let easeReason: FailReason | null = null;
  const unlinked = new Set<number>();
  let focus: Beat | null = null;
  /** The edge split (or beat-0 cut) that changed a target curve the most, and the worst one on a partner axis. */
  let splits: SplitOn | null = null;
  let partnerSplit: SplitReport = EXACT_SPLIT;

  /** New layers of this paste: `line:props:source layer` -> layer, and the layers taken per `line:prop`. */
  const newLayers = new Map<string, number>();
  const takenLayers = new Map<string, Set<number>>();
  const takenOf = (line: LineId, prop: LineProp): Set<number> => {
    const k = `${String(line)}:${prop}`;
    let set = takenLayers.get(k);
    if (set === undefined) {
      set = new Set<number>();
      takenLayers.set(k, set);
    }
    return set;
  };
  const newLayerFor = (line: LineId, lineRec: Line, prop: LineProp, from: number): number => {
    // On an X/Y-linked line both position axes of a source layer go to one layer that is free on both, so the
    // pasted axes stay a pair (same layer, keys at the same beats).
    const props: readonly LineProp[] =
      (prop === 'moveX' || prop === 'moveY') && lineRec.editor.xyLinked ? ['moveX', 'moveY'] : [prop];
    const key = `${String(line)}:${props.join('+')}:${String(from)}`;
    const known = newLayers.get(key);
    if (known !== undefined) return known;
    const taken = new Set<number>();
    for (const p of props) {
      for (const l of takenOf(line, p)) taken.add(l);
      for (const l of layersInUse(tx, line, p)) taken.add(l);
    }
    const free = freeLayer(tx, line, prop, taken) ?? tx.fail(reason(MOTION_MSG.noFreeLayer));
    newLayers.set(key, free);
    for (const p of props) takenOf(line, p).add(free);
    return free;
  };

  /** Where position axes of X/Y-linked lines changed (`line:layer`), aligned with each other at the end. */
  const xyWindows = new Map<string, XyWindow>();
  const noteXy = (info: TrackInfo, before: ReadonlySet<string>, recs: readonly KeyRec[], span: Span | null): void => {
    if (xyPartnerOf(info) === null || info.line === null || info.prop === null) return;
    const key = `${String(info.line.id)}:${String(info.layer)}`;
    let w = xyWindows.get(key);
    if (w === undefined) {
      w = { line: info.line.id, layer: info.layer, spans: [], beats: new Map(), edited: new Set() };
      xyWindows.set(key, w);
    }
    w.edited.add(info.prop);
    if (span !== null) w.spans.push([span[0], span[1]]);
    for (const k of recs) if (!before.has(Beats.key(k.t))) w.beats.set(Beats.key(k.t), k.t);
  };

  for (let r = 0; r < m.repeat; r++) {
    const shift = Beats.add(base, Beats.mulInt(every, r, tx.rounding), tx.rounding);
    const at = (rel: Beat): Beat => Beats.add(shift, rel, tx.rounding);

    for (const cn of payload.notes) {
      const line = lineFor(cn.lineSlot);
      const lineRec = line === null ? undefined : tx.doc.lines.get(line);
      if (line === null || lineRec === undefined) {
        skipped++;
        continue;
      }
      if (isUiMover(lineRec)) {
        uiMoverSkipped++;
        continue;
      }
      const t = at(cn.note.t);
      if (Beats.cmp(t, Beats.ZERO) < 0) {
        skipped++;
        continue;
      }
      const end = cn.note.kind === 'hold' ? at(cn.note.end) : t;
      let hitsound = cn.note.hitsound;
      if (!hitsoundAvailable(tx, hitsound)) {
        hitsound = null;
        hitsoundsDropped++;
      }
      const x = tidy((m.mirror ? -cn.note.x : cn.note.x) + xShift);
      const id = tx.newId('notes');
      const { src: _src, ...fields } = cn.note;
      tx.insert('notes', { ...fields, id, line, t, end, x, hitsound, gen: null });
      newNotes.push(id);
      if (focus === null || Beats.lt(t, focus)) focus = t;
    }

    for (const slice of payload.tracks) {
      const line = lineFor(slice.lineSlot);
      if (line === null) {
        skipped += slice.keys.length;
        continue;
      }
      const spec = PROP_SPECS[slice.prop];
      const lineRec = tx.doc.lines.get(line) as Line;
      // A property the target line does not use is not created there (it would change what the line is, or be
      // ignored by Phira); GIF progress needs a GIF image.
      const applies =
        slice.prop === 'gif'
          ? hasGifTexture(tx.doc, lineRec)
          : propAppliesTo(slice.prop, kindOfLine(tx.doc, tx.idx, lineRec));
      const layer = !spec.layered
        ? 0
        : m.asNewLayer
          ? newLayerFor(line, lineRec, slice.prop, slice.layer)
          : slice.layer;
      const keys: SliceKey[] = slice.keys.map((k: ClipKey) => ({
        t: at(k.t),
        vIn: m.mirror ? mirrorValue(k.vIn, slice.prop) : k.vIn,
        vOut: m.mirror ? mirrorValue(k.vOut, slice.prop) : k.vOut,
        ease: k.ease,
        link: 0,
      }));
      const owner: TrackOwner = { kind: 'line', line, prop: slice.prop, layer };
      const existing = tx.idx.trackOfOwner(owner);
      if (!applies && (existing === undefined || slice.prop === 'gif')) {
        skipped += keys.length;
        continue;
      }
      if (m.addValues && !m.asNewLayer) {
        if (existing === undefined) {
          unmatched += keys.length;
          continue;
        }
        const info = requireTrack(tx, existing);
        const recs = readKeys(tx, existing);
        const before = beatSet(recs);
        const lastJ = keys.length - 1;
        // Only the slice's own segments change (RPE adds an event's start and end values to the matching
        // event): its first key adds to the leave value, its last key to the arrive value, so the segments
        // arriving at and leaving the pasted span keep their values. A single key is a point: both values.
        const addIn = (j: number): boolean => lastJ === 0 || j > 0;
        const addOut = (j: number): boolean => lastJ === 0 || j < lastJ;
        // The target keys are matched before anything is inserted (a lead-in key is no paste target).
        const targets = keys.map((k) => recs[indexAt(recs, k.t)]);
        const [head, second] = recs;
        // Changed values of the first segment would also change the lead-in Phira extrapolates from it before
        // the first key: that lead-in becomes an explicit segment first (motionTime.ts `explicitLeadIn`).
        const firstSegmentChanges = keys.some((k, j) => {
          const rec = targets[j];
          if (rec === undefined) return false;
          // The second key's arrive value also moves when only its leave value is added and it has no jump.
          const arriveFollows = !addIn(j) && head?.ease.kind !== 'hold' && sameValue(rec.vIn, rec.vOut);
          return (
            (rec === head && addOut(j) && addChanges(rec.vOut, k.vOut, info)) ||
            (rec === second && addIn(j) && addChanges(rec.vIn, k.vIn, info)) ||
            (rec === second && arriveFollows && addChanges(rec.vOut, k.vOut, info))
          );
        });
        if (firstSegmentChanges && head !== undefined) {
          splits = collectSplit(splits, info, explicitLeadIn(recs, head.t, curveCtx(tx, info), unlinked));
        }
        keys.forEach((k, j) => {
          const rec = targets[j];
          const vIn = rec === undefined ? null : addIn(j) ? addValue(rec.vIn, k.vIn, info.valueType) : rec.vIn;
          const vOut = rec === undefined ? null : addOut(j) ? addValue(rec.vOut, k.vOut, info.valueType) : rec.vOut;
          if (rec === undefined || vIn === null || vOut === null) {
            unmatched++;
            return;
          }
          // Continuity (UX-033, RPE autostick EVT-14): a target key without a jump moves as a whole, so the
          // segments next to the pasted span follow its new value instead of snapping back through a silent jump.
          // A key after a Stay keeps its arrive value (the hold invariant): there the edit is a visible jump.
          const ri = recs.indexOf(rec);
          const prevRec = recs[ri - 1];
          // The track's original first key has no segment before it (only an imported lead-in that was just made
          // explicit, whose curve stays as it was).
          const keepContinuous =
            sameValue(rec.vIn, rec.vOut) && (prevRec === undefined || (rec !== head && prevRec.ease.kind !== 'hold'));
          if (addOut(j)) {
            rec.vIn = keepContinuous && !addIn(j) ? vOut : vIn;
            rec.vOut = vOut;
          } else if (keepContinuous) {
            rec.vIn = vIn;
            rec.vOut = vIn;
          } else {
            // Only the arrive value changes; the target's last key (without a jump) holds the new end
            // afterwards, as Phira holds the last event's end.
            setArrive(recs, ri, vIn);
          }
          if (rec.id !== null) {
            pastedKeys.push(rec.id);
            added.push(rec.id);
          }
        });
        commitKeys(tx, info, recs);
        noteXy(info, before, recs, null);
        continue;
      }
      if (keys.some((k) => !isTrackValueOf(spec.valueType, k.vIn) || !isTrackValueOf(spec.valueType, k.vOut))) {
        skipped += keys.length;
        continue;
      }
      const target = ownerInfo(tx, owner);
      if (target === null) {
        skipped += keys.length;
        continue;
      }
      // Pasted keys are authored keys: their eases pass the same guards as setEase (no handle x outside 0..1, no
      // slice Phira would divide by zero in, no Bezier on speed, no overshoot on a type-on / erase text).
      const easeWhy = sliceEaseReason(tx, target.info, keys);
      if (easeWhy !== null) {
        skipped += keys.length;
        easeReason ??= easeWhy;
        continue;
      }
      const clipped = clipAtZero(keys, curveCtx(tx, target.info));
      skipped += clipped.dropped;
      const pasted = clipped.keys;
      if (pasted.length === 0) continue;
      const info = target.exists ? target.info : createTrack(tx, owner);
      splits = collectSplit(splits, info, clipped.report);
      const recs = target.exists ? readKeys(tx, info.track.id) : [];
      const before = beatSet(recs);
      if (!target.exists) {
        for (const k of pasted) recs.push(newRec(k.t, k.vIn, k.vOut, k.ease));
      } else {
        splits = collectSplit(splits, info, replaceSpan(recs, pasted, curveCtx(tx, info), unlinked));
      }
      commitKeys(tx, info, recs);
      const s = (pasted[0] as SliceKey).t;
      const e = (pasted[pasted.length - 1] as SliceKey).t;
      pastedSpans.push({ track: info.track.id, s, e });
      noteXy(info, before, recs, [s, e]);
      if (focus === null || Beats.lt(s, focus)) focus = s;
    }
  }
  // X/Y link: both position axes share their beats inside the pasted spans (and at beats an axis gained).
  for (const w of xyWindows.values()) {
    const aligned = alignXyBeats(tx, w, unlinked);
    splits = worseSplitOn(splits, aligned.edited);
    partnerSplit = worseSplit(partnerSplit, aligned.partner);
  }
  for (const p of pastedSpans) {
    for (const id of tx.idx.keysByTrack(p.track)) {
      const k = tx.doc.keys.get(id);
      if (k !== undefined && Beats.lte(p.s, k.t) && Beats.lte(k.t, p.e)) pastedKeys.push(id);
    }
  }
  cleanLinks(tx, unlinked);
  syncLinkGroups(
    tx,
    added.flatMap((id) => linkedNeighbours(tx, id)),
  );
  if (uiMoverSkipped > 0) tx.note(reason(MOTION_MSG.pasteNotesOnUiMover, { count: uiMoverSkipped }));
  if (hitsoundsDropped > 0) tx.note(reason(MOTION_MSG.pasteHitsoundsDropped, { count: hitsoundsDropped }));
  if (unmatched > 0) tx.note(reason(MOTION_MSG.pasteUnmatched, { count: unmatched }));
  if (skipped > 0) tx.note(reason(MOTION_MSG.pasteSkipped, { count: skipped }));
  if (easeReason !== null) tx.note(easeReason);
  noteWorstSplit(tx, splits);
  notePartnerSplit(tx, partnerSplit);
  const keys = [...new Set(pastedKeys)].filter((id) => tx.doc.keys.has(id));
  // Nothing pasted: an ease the target does not allow is the reason worth showing (as setEase does).
  if (newNotes.length === 0 && keys.length === 0) tx.fail(easeReason ?? reason(MOTION_MSG.pasteNothing));
  if (focus !== null) tx.setFocus(m.toOriginalLines ? { beat: focus } : { beat: focus, line: targetLine });
  return { notes: newNotes, keys, unmatched: unmatched + skipped + uiMoverSkipped };
};

// ---------------------------------------------------------------------------------------------------------------
// Paste whole lines
// ---------------------------------------------------------------------------------------------------------------

function groupFor(tx: TxBuilder, name: string | null, created: Map<string, GroupId>): GroupId | null {
  if (name === null) return null;
  for (const g of tx.doc.groups.values()) if (g.name === name) return g.id;
  const known = created.get(name);
  if (known !== undefined) return known;
  let order = 0;
  for (const g of tx.doc.groups.values()) order = Math.max(order, g.order + 1);
  const id = tx.newId('groups');
  tx.insert('groups', { id, name, order, color: null });
  created.set(name, id);
  return id;
}

const pasteLines: ClipboardCommands['pasteLines'] = (tx, { payload, after }) => {
  const clip = payload.wholeLines;
  if (clip.length === 0) tx.fail(reason(MOTION_MSG.pasteLinesNothing));
  const ids: LineId[] = clip.map(() => tx.newId('lines'));
  const missing = new Set<AssetId>();
  const createdGroups = new Map<string, GroupId>();
  const linkMap = new Map<number, number>();
  let nextLink = nextLinkId(tx.doc);
  const freshLink = (old: number): number => {
    if (old === 0) return 0;
    let l = linkMap.get(old);
    if (l === undefined) {
      l = nextLink++;
      linkMap.set(old, l);
    }
    return l;
  };

  clip.forEach((cl, i) => {
    const id = ids[i] as LineId;
    let texture = cl.line.texture;
    if (texture !== null && !tx.doc.assets.has(texture)) {
      missing.add(texture);
      texture = null;
    }
    const gifTexture = texture !== null && isGifAsset(tx.doc.assets.get(texture));
    const hasGifTrack = cl.tracks.some((t) => t.prop === 'gif');
    const parentSlot = cl.parentSlot;
    const parent = parentSlot === null || parentSlot === i ? null : (ids[parentSlot] ?? null);
    const line: Line = {
      ...cl.line,
      id,
      parent,
      group: groupFor(tx, cl.groupName, createdGroups),
      texture,
      gifAutoLoop: cl.line.gifAutoLoop && gifTexture && !hasGifTrack,
    };
    tx.insert('lines', line);
  });

  clip.forEach((cl, i) => {
    const id = ids[i] as LineId;
    const line = tx.doc.lines.get(id) as Line;
    const gifTexture = hasGifTexture(tx.doc, line);
    for (const ct of cl.tracks) {
      if (ct.prop === 'gif' && !gifTexture) continue;
      const spec = PROP_SPECS[ct.prop];
      const layer = spec.layered ? ct.layer : 0;
      const first = ct.keys[0];
      if (first === undefined) continue;
      const importedLeadIn = requiresLeadInKey(ct.prop) && Beats.cmp(first.t, Beats.ZERO) > 0;
      const trackId = tx.newId('tracks');
      const track: Track = importedLeadIn
        ? {
            id: trackId,
            owner: { kind: 'line', line: id, prop: ct.prop, layer },
            valueType: spec.valueType,
            importedLeadIn: true,
          }
        : { id: trackId, owner: { kind: 'line', line: id, prop: ct.prop, layer }, valueType: spec.valueType };
      tx.insert('tracks', track);
      const recs: KeyRec[] = ct.keys.map((k) => newRec(k.t, k.vIn, k.vOut, k.ease, freshLink(k.link)));
      commitKeys(tx, infoOf(tx, track), recs, { leadIn: false });
    }
    for (const n of cl.notes) {
      const noteId = tx.newId('notes');
      let hitsound = n.hitsound;
      if (hitsound?.kind === 'asset' && !tx.doc.assets.has(hitsound.asset)) {
        missing.add(hitsound.asset);
        hitsound = null;
      }
      tx.insert('notes', { ...n, id: noteId, line: id, hitsound, gen: null });
    }
  });

  // Export order: after `after` (or at the end).
  const order = [...tx.doc.lineOrder].filter((l) => !ids.includes(l));
  const pos = after === undefined ? -1 : order.indexOf(after);
  const insertAt = pos < 0 ? order.length : pos + 1;
  order.splice(insertAt, 0, ...ids);
  if (!valueEquals(order, tx.doc.lineOrder)) tx.setOrder('lineOrder', order);
  cleanLinks(tx, linkMap.values());
  if (missing.size > 0) tx.note(reason(MOTION_MSG.pasteLinesMissingAssets, { count: missing.size }));
  tx.setSelection({ lines: ids, activeLine: ids[0] ?? null });
  return { lines: ids, missingAssets: [...missing] };
};

export const clipboardCommands: ClipboardCommands = {
  copy,
  paste,
  pasteLines,
  parseClipboardText: (text) => parseClipText(text),
  serializeClipboard: (p: ClipboardPayload) => serializeClip(p),
};
